import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { Divider, Group, Popover, px, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ResourceAvatar, useMedplum } from '@medplum/react';
import { IconSearch, IconX } from '@tabler/icons-react';
import { ReactElement, useCallback, useState } from 'react';
import { TimelineCommunicationResource } from '@/pages/PatientTimelinePage/graphql';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { TeammateAvatar } from '@/components/TeamAvatar';
interface AssigneeAvatarProps {
  communication: TimelineCommunicationResource;
  /**
   * Controlled popover state. Lets a Reassign icon elsewhere in the header open
   * the SAME picker the avatar anchors — one popover, two doorways, the way the
   * email card's assignee slot works. Omit both props and the avatar keeps its
   * own state, which is what the message rows do.
   */
  opened?: boolean;
  onOpenedChange?: (opened: boolean) => void;
}

export function AssigneeAvatar({
  communication,
  opened: controlledOpened,
  onOpenedChange,
}: AssigneeAvatarProps): ReactElement {
  const medplum = useMedplum();

  const { careTeamContext } = useTimelineContext();
  const { careTeamProviders } = careTeamContext ?? {};
  const [uncontrolledOpened, setUncontrolledOpened] = useState(false);
  const opened = controlledOpened ?? uncontrolledOpened;
  const setOpened = useCallback(
    (next: boolean) => (onOpenedChange ? onOpenedChange(next) : setUncontrolledOpened(next)),
    [onOpenedChange]
  );
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const assignee = communication.assigneeTask?.[0]?.owner?.resource;
  const assigneeRefString: string | undefined = communication.assigneeTask?.[0]?.owner?.reference;

  const filteredProviders = (careTeamProviders ?? []).filter((practitioner) => {
    if (!searchQuery.trim()) return true;
    const name = formatHumanName(practitioner.name?.[0]);
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleAssigneeChange = useCallback(
    async (practitioner: Practitioner) => {
      if (isAssigning) return;

      try {
        setIsAssigning(true);

        // Call the assign-communication bot
        await fetchBot(medplum, 'assign-communication', {
          communicationRef: { reference: getReferenceString(communication) },
          providerRef: { reference: getReferenceString(practitioner) },
        });

        setOpened(false);
      } catch (error) {
        alert.error(`Couldn't assign communication: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsAssigning(false);
      }
    },
    [isAssigning, medplum, communication]
  );

  const handleRemoveAssignee = useCallback(async () => {
    if (isAssigning || !assignee) return;

    try {
      setIsAssigning(true);

      // Call the unassign-communication bot
      await fetchBot(medplum, 'assign-communication', {
        communicationRef: createReference(communication),
        providerRef: null,
      });

      setOpened(false);
    } catch (error) {
      alert.error(`Couldn't unassign communication: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAssigning(false);
    }
  }, [isAssigning, assignee, medplum, communication]);

  const assigneeName = formatHumanName(assignee?.name?.[0]) || 'Unassigned';

  return (
    <Popover width={220} position="bottom-end" withArrow shadow="md" opened={opened} onChange={setOpened}>
      <Popover.Target>
        <Tooltip label={assigneeName} withArrow>
          <UnstyledButton
            onClick={() => setOpened(!opened)}
            disabled={isAssigning}
            aria-label={`Reassign — ${assigneeName}`}
          >
            <TeammateAvatar practitioner={assignee || undefined} />
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown
        style={{
          padding: '8px 8px',
        }}
      >
        <Stack gap={2}>
          <Text size="sm" fw={500} c="dimmed">
            Assign to:
          </Text>
          <TextInput
            placeholder="Search practitioners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            size="xs"
            mb="xs"
            leftSection={<IconSearch size={14} />}
          />
          {(() => {
            return (
              <Stack gap={2} style={{ maxHeight: px(195), overflowY: 'auto' }}>
                {/* Remove assignee option - only show if there's a current assignee */}
                {assignee && (
                  <>
                    <UnstyledButton
                      onClick={handleRemoveAssignee}
                      disabled={isAssigning}
                      style={{
                        padding: '4px 2px',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.2s ease',
                        opacity: isAssigning ? 0.6 : 1,
                      }}
                      w="100%"
                      onMouseEnter={(e) => {
                        if (!isAssigning) {
                          e.currentTarget.style.backgroundColor = '#f8d7da';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAssigning) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Group gap={8} style={{ minWidth: 0 }}>
                        <IconX size={20} color="#dc3545" />
                        <Text
                          size="sm"
                          c="#dc3545"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          Remove assignee
                        </Text>
                      </Group>
                    </UnstyledButton>
                    <Divider size="xs" />
                  </>
                )}
                {filteredProviders.map((practitioner) => {
                  const isCurrentAssignee = assigneeRefString === getReferenceString(practitioner);

                  return (
                    <UnstyledButton
                      key={practitioner.id}
                      onClick={() => handleAssigneeChange(practitioner)}
                      disabled={isAssigning}
                      style={{
                        padding: '4px 2px',
                        borderRadius: '4px',
                        backgroundColor: isCurrentAssignee ? '#f8f9fa' : 'transparent',
                        transition: 'background-color 0.2s ease',
                        opacity: isAssigning ? 0.6 : 1,
                      }}
                      w="100%"
                      onMouseEnter={(e) => {
                        if (!isCurrentAssignee && !isAssigning) {
                          e.currentTarget.style.backgroundColor = '#f1f3f4';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrentAssignee && !isAssigning) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Group gap={8} style={{ minWidth: 0 }}>
                        <ResourceAvatar value={practitioner} size="sm" />
                        <Text
                          size="sm"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {formatHumanName(practitioner.name?.[0])}
                        </Text>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            );
          })()}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
